/**
 * Core Branch Inventory State Manager & Validator
 * Handles customer branch selection, location inventory queries, and cart re-validation.
 */
(function() {
  const STORAGE_KEY_BRANCH = 'selected_branch';
  const STORAGE_KEY_STORE_NAME = 'store_name';
  const STORAGE_KEY_SESSION_TIME = 'store_session_time';

  // Cache for inventory lookups to prevent redundant network requests
  const inventoryCache = new Map();

  const BranchInventory = {
    /**
     * Get currently selected branch object
     * @returns {Object|null} { id, name, locationId }
     */
    getSelectedBranch: function() {
      try {
        const storedJson = localStorage.getItem(STORAGE_KEY_BRANCH);
        if (storedJson) {
          const parsed = JSON.parse(storedJson);
          if (parsed && parsed.name) {
            // Auto-heal locationId if missing, non-numeric/equal to name, or mapped in config
            if (window.getLocationIdForBranch) {
              const mappedId = window.getLocationIdForBranch(parsed.name || parsed.id);
              if (mappedId && mappedId !== parsed.name && mappedId !== parsed.id) {
                if (parsed.locationId !== mappedId) {
                  parsed.locationId = mappedId;
                  localStorage.setItem(STORAGE_KEY_BRANCH, JSON.stringify(parsed));
                }
              }
            }
            if (parsed.locationId) {
              return parsed;
            }
          }
        }
      } catch (e) {
        console.warn('Failed to parse selected_branch from localStorage:', e);
      }

      // Fallback: check legacy store_name
      const legacyName = localStorage.getItem(STORAGE_KEY_STORE_NAME);
      if (legacyName) {
        const config = window.getBranchConfig ? window.getBranchConfig(legacyName) : null;
        const branchObj = {
          id: legacyName,
          name: legacyName,
          locationId: config ? config.locationId : legacyName
        };
        return branchObj;
      }

      return null;
    },

    /**
     * Set active branch and sync cart attributes
     * @param {Object|string} branchData - Branch object or branch name string
     */
    setSelectedBranch: function(branchData) {
      let branchObj = null;

      if (typeof branchData === 'string') {
        const config = window.getBranchConfig ? window.getBranchConfig(branchData) : null;
        branchObj = {
          id: branchData,
          name: branchData,
          locationId: config ? config.locationId : branchData
        };
      } else if (branchData && typeof branchData === 'object') {
        const config = window.getBranchConfig ? window.getBranchConfig(branchData.name || branchData.id) : null;
        const validMappedId = (config && config.locationId && config.locationId !== config.name && config.locationId !== config.name_ar) ? config.locationId : '';
        const passedLocId = (branchData.locationId && branchData.locationId !== branchData.name) ? branchData.locationId : '';
        branchObj = {
          id: branchData.id || branchData.name,
          name: branchData.name || branchData.id,
          locationId: validMappedId || passedLocId || branchData.locationId || ''
        };
      }

      if (!branchObj || !branchObj.name) return;

      // Invalidate inventory cache on branch change
      inventoryCache.clear();

      // Save in localStorage while preserving legacy keys
      localStorage.setItem(STORAGE_KEY_BRANCH, JSON.stringify(branchObj));
      localStorage.setItem(STORAGE_KEY_STORE_NAME, branchObj.name);
      localStorage.setItem(STORAGE_KEY_SESSION_TIME, Date.now());

      // Sync with Shopify Cart Attributes
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            store_name: branchObj.name,
            branch_id: branchObj.id,
            shopify_location_id: branchObj.locationId
          }
        })
      }).catch(err => console.error('Failed to update cart attributes:', err));

      // Emit branch:changed custom event
      window.dispatchEvent(new CustomEvent('branch:changed', { detail: branchObj }));
      return branchObj;
    },

    /**
     * Get inventory for a specific variant at a specific location
     * @param {number|string} variantId 
     * @param {string} locationId 
     * @returns {Promise<number>} Available quantity (Infinity if untracked, 0 if out of stock)
     */
    getLocationInventory: async function(variantId, locationId) {
      if (!variantId) return 0;
      const activeBranch = locationId ? { locationId } : this.getSelectedBranch();
      const locId = activeBranch ? activeBranch.locationId : '';
      const branchName = activeBranch ? (activeBranch.name || activeBranch.id) : '';

      const cacheKey = `${variantId}_${locId}_${branchName}`;
      if (inventoryCache.has(cacheKey)) {
        const cached = inventoryCache.get(cacheKey);
        if (Date.now() - cached.timestamp < 30000) {
          return cached.quantity;
        }
      }

      try {
        // 1. Check Storefront GraphQL API if Storefront Token is set
        if (window.storefrontApiToken) {
          const query = `
            query getVariantInventory($id: ID!) {
              node(id: $id) {
                ... on ProductVariant {
                  id
                  availableForSale
                  storeAvailability(first: 20) {
                    nodes {
                      available
                      quantityAvailable
                      location {
                        id
                        name
                      }
                    }
                  }
                }
              }
            }
          `;
          const gid = String(variantId).startsWith('gid://') 
            ? variantId 
            : `gid://shopify/ProductVariant/${variantId}`;

          const response = await fetch('/api/2024-01/graphql.json', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': window.storefrontApiToken
            },
            body: JSON.stringify({ query, variables: { id: gid } })
          });

          const json = await response.json();
          const variantData = json?.data?.node;
          if (variantData && variantData.storeAvailability) {
            const nodes = variantData.storeAvailability.nodes || [];
            const match = nodes.find(n => 
              String(n.location.id) === String(locId) || 
              String(n.location.id).endsWith(`/${locId}`) ||
              (branchName && n.location.name && n.location.name.toLowerCase() === branchName.toLowerCase())
            );
            if (match) {
              const qty = match.quantityAvailable !== undefined && match.quantityAvailable !== null
                ? match.quantityAvailable
                : (match.available ? Infinity : 0);

              inventoryCache.set(cacheKey, { quantity: qty, timestamp: Date.now() });
              return qty;
            } else {
              inventoryCache.set(cacheKey, { quantity: 0, timestamp: Date.now() });
              return 0;
            }
          }
        }

        // 2. Query Shopify's native pickup-availability section
        const rootUrl = (window.routes && typeof window.routes.root_url === 'string') ? window.routes.root_url : '/';
        const prefix = rootUrl.endsWith('/') ? rootUrl : rootUrl + '/';
        const url = `${prefix}variants/${variantId}/?section_id=pickup-availability`;

        console.log('[BranchInventory] Fetching pickup availability:', { variantId, locId, branchName, url });

        const res = await fetch(url);
        if (res.ok) {
          const text = await res.text();
          console.log('[BranchInventory] Pickup response status:', res.status, 'HTML bytes:', text.length);
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const listItems = doc.querySelectorAll('.pickup-availability-list__item, li');
          console.log('[BranchInventory] Total locations in response:', listItems.length);

          if (listItems.length === 0) {
            console.warn('[BranchInventory] Shopify returned 0 locations for this variant in pickup availability HTML. Check if Local Pickup is enabled in Settings > Shipping and delivery > Local pickup.');
          }

          let found = false;
          let isAvailable = false;

          listItems.forEach(item => {
            const h3 = item.querySelector('h3');
            const locName = (h3 ? h3.textContent : (item.getAttribute('data-location-name') || '')).trim().toLowerCase();
            const itemLocId = item.getAttribute('data-location-id') || '';
            const isDataAvailable = item.getAttribute('data-available');
            const preview = item.querySelector('.pickup-availability-preview');
            const previewText = preview ? preview.textContent.trim().toLowerCase() : '';

            const bName = branchName ? branchName.toLowerCase() : '';

            // 1. Exact or suffix Location ID match
            const isIdMatch = locId && itemLocId && (
              String(itemLocId) === String(locId) ||
              String(itemLocId).endsWith(`/${locId}`)
            );

            // 2. Flexible Name & Token match
            const config = window.getBranchConfig ? window.getBranchConfig(branchName) : null;
            const configName = config ? (config.name || '').toLowerCase() : '';
            const configNameAr = config ? (config.name_ar || '').toLowerCase() : '';

            const isNameMatch = (function() {
              if (!locName) return false;
              const namesToTest = [bName, configName, configNameAr].filter(Boolean);
              for (const name of namesToTest) {
                if (locName === name || locName.includes(name) || name.includes(locName)) return true;

                const normalize = s => s.toLowerCase().replace(/al\s+/g, '').replace(/pharmacy/g, '').replace(/صيدلية/g, '').replace(/فرع/g, '').replace(/[^a-z0-9\u0600-\u06FF]/g, ' ').trim();
                const locTokens = normalize(locName).split(/\s+/).filter(w => w.length > 2);
                const nameTokens = normalize(name).split(/\s+/).filter(w => w.length > 2);

                const tokenMatch = locTokens.some(lt => nameTokens.some(nt => {
                  if (lt === nt || lt.includes(nt) || nt.includes(lt)) return true;
                  const ltFuzzy = lt.replace(/b/g, 'p');
                  const ntFuzzy = nt.replace(/b/g, 'p');
                  return ltFuzzy === ntFuzzy;
                }));

                if (tokenMatch) return true;
              }
              return false;
            })();

            console.log('[BranchInventory] Item evaluated:', {
              renderedName: locName,
              renderedLocationId: itemLocId,
              isAvailableAttribute: isDataAvailable,
              isIdMatch,
              isNameMatch
            });

            if (isIdMatch || isNameMatch) {
              found = true;
              if (isDataAvailable === 'true' || (preview && (preview.querySelector('.svg-wrapper') || previewText.includes('available') || previewText.includes('متوفر')))) {
                isAvailable = true;
              }
            }
          });

          if (found) {
            const qty = isAvailable ? Infinity : 0;
            console.log('[BranchInventory] Result:', { found, isAvailable, qty });
            inventoryCache.set(cacheKey, { quantity: qty, timestamp: Date.now() });
            return qty;
          } else {
            console.warn('[BranchInventory] No matching location found in pickup availability list for:', branchName, locId);
          }
        }

        // 3. Query Backend / App Proxy endpoint if available
        const appProxyUrl = `/apps/branch-inventory/stock?variant_id=${variantId}&location_id=${locId}`;
        const proxyRes = await fetch(appProxyUrl, { headers: { 'Accept': 'application/json' } });
        if (proxyRes.ok) {
          const proxyData = await proxyRes.json();
          if (proxyData && typeof proxyData.available_quantity === 'number') {
            const qty = proxyData.available_quantity;
            inventoryCache.set(cacheKey, { quantity: qty, timestamp: Date.now() });
            return qty;
          }
        }
      } catch (e) {
        console.warn('Location inventory lookup error:', e);
      }

      // If no store availability record is found for this location, return 0 (Out of Stock)
      inventoryCache.set(cacheKey, { quantity: 0, timestamp: Date.now() });
      return 0;
    },

    /**
     * Validate product quantity requested against active location stock
     * @param {number|string} variantId 
     * @param {number} requestedQty 
     * @returns {Promise<{ allowed: boolean, availableQty: number, message: string }>}
     */
    validateProductAvailability: async function(variantId, requestedQty) {
      const isArabic = document.documentElement.lang.includes('ar');
      const branch = this.getSelectedBranch();

      if (!branch) {
        return {
          allowed: false,
          availableQty: 0,
          message: isArabic 
            ? 'يرجى اختيار الصيدلية قبل إضافة المنتجات إلى السلة.' 
            : 'Please select a pharmacy before adding products to your cart.'
        };
      }

      const availableQty = await this.getLocationInventory(variantId, branch.locationId);

      if (availableQty <= 0) {
        return {
          allowed: false,
          availableQty: 0,
          message: isArabic ? 'المنتج غير متوفر في الصيدلية المختارة' : 'Item is out of stock at selected pharmacy'
        };
      }

      if (requestedQty > availableQty) {
        return {
          allowed: false,
          availableQty: availableQty,
          message: isArabic
            ? `متوفر ${availableQty} قطعة فقط في الصيدلية المختارة.`
            : `Only ${availableQty} units available at selected pharmacy.`
        };
      }

      return {
        allowed: true,
        availableQty: availableQty,
        message: ''
      };
    },

    /**
     * Validate all items in current cart against selected branch
     * @param {Object} cart - Shopify cart object
     * @returns {Promise<{ valid: boolean, invalidItems: Array, messages: Array }>}
     */
    validateCartAvailability: async function(cart) {
      const isArabic = document.documentElement.lang.includes('ar');
      const branch = this.getSelectedBranch();

      if (!branch) {
        return {
          valid: false,
          invalidItems: cart ? cart.items : [],
          messages: [
            isArabic 
              ? 'يرجى اختيار الصيدلية لإكمال الطلب.' 
              : 'Please select a pharmacy to proceed with checkout.'
          ]
        };
      }

      if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
        return { valid: true, invalidItems: [], messages: [] };
      }

      const invalidItems = [];
      const messages = [];

      for (const item of cart.items) {
        const available = await this.getLocationInventory(item.variant_id, branch.locationId);
        if (available < item.quantity) {
          invalidItems.push({
            item: item,
            requested: item.quantity,
            available: available
          });

          if (available <= 0) {
            messages.push(
              isArabic
                ? `المنتج "${item.title}" غير متوفر في صيدلية ${branch.name}.`
                : `Item "${item.title}" is out of stock at ${branch.name}.`
            );
          } else {
            messages.push(
              isArabic
                ? `الكمية المطلوبة من "${item.title}" (${item.quantity}) تتجاوز المتوفر (${available}) في صيدلية ${branch.name}.`
                : `Quantity for "${item.title}" (${item.quantity}) exceeds available stock (${available}) at ${branch.name}.`
            );
          }
        }
      }

      return {
        valid: invalidItems.length === 0,
        invalidItems: invalidItems,
        messages: messages
      };
    }
  };

  window.BranchInventory = BranchInventory;
})();
